import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { VictoryChart, VictoryLine, VictoryAxis, VictoryLabel } from 'victory';
import moment from 'moment';
import styled from 'styled-components';
import styles from '../../../utility/styles';

const colorScale = styles.colors.software_tracker_colors;

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    height: 400px;
    @media only screen and ${styles.breakpoints.tablet} {
        height: 300px;
    }
`

const LegendWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    justify-content: center;
`

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    opacity: ${({ dimmed }) => dimmed ? 0.3 : 1};
    transition: opacity 0.2s;
`

const LegendDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ color }) => color};
`

const LegendLabel = styled.span`
    font-family: ${styles.fonts.geist};
    font-size: 12px;
    font-weight: 600;
    color: ${styles.colors.lightGray};
`

const HoverLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
`

const HoverTarget = styled.div`
    position: absolute;
    top: 20px;
    bottom: 60px;
    left: 50px;
    right: 30px;
    pointer-events: auto;
`

const HoverLine = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: ${styles.colors.lightGray}40;
    pointer-events: none;
`

const TooltipOverlay = styled.div`
    position: absolute;
    pointer-events: none;
    z-index: 10;
    background: ${styles.colors.background};
    border: 1px solid ${styles.colors.darkGray};
    border-radius: 8px;
    padding: 10px 14px;
    min-width: 170px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
`

const TooltipDate = styled.div`
    font-family: ${styles.fonts.geist};
    font-size: 11px;
    color: ${styles.colors.mediumGray};
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid ${styles.colors.darkGray}60;
`

const TooltipRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
`

const TDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ color }) => color};
    flex-shrink: 0;
`

const TVersion = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 11px;
    color: ${styles.colors.lightGray};
    flex: 1;
`

const TPct = styled.span`
    font-family: ${styles.fonts.geistMono};
    font-size: 11px;
    font-weight: 600;
    color: ${styles.colors.white};
`

const formatTick = (tick, index, ticks) => {
    const formatted = moment(tick).format('MMM D')
    if (ticks.length > 60) return index % 14 === 0 ? formatted : ''
    if (ticks.length > 30) return index % 7 === 0 ? formatted : ''
    return index % 3 === 0 ? formatted : ''
}

// Chart padding must match VictoryChart padding for hover positioning
const PADDING = { bottom: 60, top: 20, right: 30, left: 50 }

export default function RolloutLineChart({ data = [] }) {
    const ref = useRef()
    const hoverRef = useRef()
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
    const [hiddenVersions, setHiddenVersions] = useState({})
    const [hoverX, setHoverX] = useState(null)

    const toggleVersion = useCallback((version) => {
        setHiddenVersions(prev => ({ ...prev, [version]: !prev[version] }))
    }, [])

    const visibleData = useMemo(() => {
        return data.filter(({ version }) => !hiddenVersions[version])
    }, [data, hiddenVersions])

    // Sorted unique dates across all series
    const sortedDates = useMemo(() => {
        const set = new Set()
        data.forEach(series => series.data.forEach(d => set.add(d.date)))
        return Array.from(set).sort()
    }, [data])

    // Build date -> entries lookup for tooltip
    const dateIndex = useMemo(() => {
        const idx = {}
        data.forEach((series, i) => {
            if (hiddenVersions[series.version]) return
            const color = colorScale[i % colorScale.length]
            series.data.forEach(d => {
                if (!idx[d.date]) idx[d.date] = []
                if (d.pct > 0) {
                    idx[d.date].push({ version: series.version, pct: d.pct, color })
                }
            })
        })
        Object.values(idx).forEach(arr => arr.sort((a, b) => b.pct - a.pct))
        return idx
    }, [data, hiddenVersions])

    // Compute which date is nearest to the mouse x position
    const chartWidth = dimensions.width - PADDING.left - PADDING.right
    const hoveredDate = useMemo(() => {
        if (hoverX == null || !sortedDates.length || chartWidth <= 0) return null
        const ratio = hoverX / chartWidth
        const idx = Math.round(ratio * (sortedDates.length - 1))
        return sortedDates[Math.max(0, Math.min(sortedDates.length - 1, idx))]
    }, [hoverX, sortedDates, chartWidth])

    const hoveredDateX = useMemo(() => {
        if (!hoveredDate || !sortedDates.length) return null
        const idx = sortedDates.indexOf(hoveredDate)
        if (idx < 0) return null
        return (idx / (sortedDates.length - 1)) * chartWidth
    }, [hoveredDate, sortedDates, chartWidth])

    const tooltipEntries = hoveredDate ? (dateIndex[hoveredDate] || []) : []

    const handleMouseMove = useCallback((e) => {
        if (!hoverRef.current) return
        const rect = hoverRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        setHoverX(x)
    }, [])

    const handleMouseLeave = useCallback(() => setHoverX(null), [])

    useEffect(() => {
        const handleResize = () => {
            if (ref.current) {
                setDimensions({ width: ref.current.offsetWidth, height: ref.current.offsetHeight })
            }
        }
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    if (!data.length) return null

    const showTooltip = hoveredDate && tooltipEntries.length > 0
    const absoluteX = hoveredDateX != null ? hoveredDateX + PADDING.left : null
    const tooltipLeft = absoluteX != null
        ? (absoluteX > dimensions.width / 2 ? absoluteX - 200 : absoluteX + 20)
        : 0

    return (
        <>
            <Wrapper ref={ref}>
                {dimensions.width > 0 && (
                    <VictoryChart
                        {...dimensions}
                        padding={PADDING}
                    >
                        <VictoryAxis
                            dependentAxis
                            tickFormat={(tick) => `${tick}%`}
                            style={{
                                tickLabels: { fill: styles.colors.lightGray, fontSize: 11, fontFamily: styles.fonts.geistMono },
                                axis: { stroke: styles.colors.transparent },
                                grid: { stroke: styles.colors.darkGray, strokeDasharray: '4,4' }
                            }}
                        />
                        <VictoryAxis
                            style={{
                                tickLabels: { fill: styles.colors.lightGray, fontSize: 10, fontFamily: styles.fonts.geistMono },
                                axis: { stroke: styles.colors.darkGray }
                            }}
                            tickLabelComponent={<VictoryLabel angle={-45} textAnchor='end' />}
                            tickFormat={(...args) => formatTick(...args)}
                        />
                        {visibleData.map((series, i) => {
                            const originalIndex = data.findIndex(d => d.version === series.version)
                            const color = colorScale[originalIndex % colorScale.length]
                            return (
                                <VictoryLine
                                    key={series.version}
                                    data={series.data.map(d => ({ x: d.date, y: d.pct }))}
                                    style={{ data: { stroke: color, strokeWidth: 2 } }}
                                    interpolation="monotoneX"
                                />
                            )
                        })}
                    </VictoryChart>
                )}
                <HoverLayer>
                    <HoverTarget
                        ref={hoverRef}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    />
                </HoverLayer>
                {showTooltip && hoveredDateX != null && (
                    <HoverLine style={{ left: absoluteX }} />
                )}
                {showTooltip && (
                    <TooltipOverlay style={{ left: tooltipLeft, top: 30 }}>
                        <TooltipDate>{moment(hoveredDate).format('MMM D, YYYY')}</TooltipDate>
                        {tooltipEntries.map(e => (
                            <TooltipRow key={e.version}>
                                <TDot color={e.color} />
                                <TVersion>{e.version}</TVersion>
                                <TPct>{e.pct.toFixed(1)}%</TPct>
                            </TooltipRow>
                        ))}
                    </TooltipOverlay>
                )}
            </Wrapper>
            <LegendWrapper>
                {data.map((series, i) => (
                    <LegendItem
                        key={series.version}
                        dimmed={hiddenVersions[series.version]}
                        onClick={() => toggleVersion(series.version)}
                    >
                        <LegendDot color={colorScale[i % colorScale.length]} />
                        <LegendLabel>{series.version}</LegendLabel>
                    </LegendItem>
                ))}
            </LegendWrapper>
        </>
    )
}
